import { createTranslationHook } from '../i18nFactory';
import { TEXTO_COMUN_ES, TEXTO_COMUN_EN } from '../commonTexts';
import type { TranslateFn } from '../i18nFactory';

/**
 * i18n del widget de Instrumento Financiero (FEAT-105-17).
 *
 * Usa `useTranslation` de siesa-ui-kit vía `createTranslationHook`; si el host no
 * registró las claves `financial_instrument.*` (el motor devuelve la propia clave),
 * cae al diccionario local del idioma activo y, en último término, al español.
 * Garantiza texto listo para usar sin depender de que el documento consumidor
 * registre los recursos. Las claves siguen el formato plano que espera
 * `DefaultTranslationEngine`.
 */

export const FINANCIAL_INSTRUMENT_TRANSLATIONS_ES: Record<string, string> = {
  'financial_instrument.panel.title': 'Instrumento financiero',
  'financial_instrument.panel.loading': 'Cargando instrumento financiero…',
  'financial_instrument.panel.loadError': 'No se pudo cargar el instrumento financiero.',
  'financial_instrument.panel.readonly': 'Instrumento financiero (solo lectura)',

  'financial_instrument.field.type': 'Tipo de instrumento',
  'financial_instrument.field.typePlaceholder': 'Seleccione un tipo',
  'financial_instrument.field.number': 'Número',
  'financial_instrument.field.numberPlaceholder': 'Número del instrumento',
  'financial_instrument.field.issueDate': 'Fecha de emisión',
  'financial_instrument.field.dueDate': 'Fecha de vencimiento',
  'financial_instrument.field.beneficiary': 'Beneficiario',
  'financial_instrument.field.beneficiaryPlaceholder': 'Nombre del beneficiario',
  'financial_instrument.field.amount': 'Monto',
  'financial_instrument.field.amountAlt': 'Monto (moneda alterna)',

  'financial_instrument.actions.create': TEXTO_COMUN_ES.save,
  'financial_instrument.actions.update': 'Guardar cambios',
  'financial_instrument.actions.delete': TEXTO_COMUN_ES.delete,

  'financial_instrument.delete.title': 'Eliminar instrumento financiero',
  'financial_instrument.delete.body':
    'Se desactivará el instrumento financiero actual. Podrá capturar uno nuevo después. ¿Continuar?',
  'financial_instrument.delete.confirm': TEXTO_COMUN_ES.delete,
  'financial_instrument.delete.cancel': TEXTO_COMUN_ES.cancel,

  'financial_instrument.validation.required': 'Campo requerido.',
  'financial_instrument.validation.amountPositive': 'El monto debe ser mayor que 0.',
  'financial_instrument.validation.dueDateBeforeIssue':
    'La fecha de vencimiento debe ser igual o posterior a la fecha de emisión.',

  'financial_instrument.error.forbidden': TEXTO_COMUN_ES.errorForbidden,
  // Sin uso: el 409 se maneja en FinancialInstrumentPanel.tsx pero con el `detail`
  // del backend. Igual que en Retenciones, distinguir el sub-caso exigiria un
  // `errorCode` que el tipo de error no declara.
  'financial_instrument.error.conflict': 'Ya existe un instrumento financiero activo para este documento.',
  'financial_instrument.error.concurrency':
    'El instrumento fue modificado por otro usuario. Se recargaron los datos.',
  'financial_instrument.error.validation': 'Revise los datos ingresados.',
  'financial_instrument.error.notFound': 'Recurso no encontrado.',
  'financial_instrument.error.generic': TEXTO_COMUN_ES.errorGeneric,

  'financial_instrument.toast.created': 'Instrumento financiero creado correctamente.',
  'financial_instrument.toast.updated': 'Instrumento financiero actualizado correctamente.',
  'financial_instrument.toast.deleted': 'Instrumento financiero eliminado correctamente.',

  'financial_instrument.factbox.title': 'Instrumento financiero',
  'financial_instrument.factbox.pending': 'Pendiente de captura',
  'financial_instrument.factbox.number': 'N° {number}',
  'financial_instrument.factbox.due': 'Vence {date}',
};

export const FINANCIAL_INSTRUMENT_TRANSLATIONS_EN: Record<string, string> = {
  'financial_instrument.panel.title': 'Financial instrument',
  'financial_instrument.panel.loading': 'Loading financial instrument…',
  'financial_instrument.panel.loadError': 'Could not load the financial instrument.',
  'financial_instrument.panel.readonly': 'Financial instrument (read-only)',

  'financial_instrument.field.type': 'Instrument type',
  'financial_instrument.field.typePlaceholder': 'Select a type',
  'financial_instrument.field.number': 'Number',
  'financial_instrument.field.numberPlaceholder': 'Instrument number',
  'financial_instrument.field.issueDate': 'Issue date',
  'financial_instrument.field.dueDate': 'Due date',
  'financial_instrument.field.beneficiary': 'Beneficiary',
  'financial_instrument.field.beneficiaryPlaceholder': 'Beneficiary name',
  'financial_instrument.field.amount': 'Amount',
  'financial_instrument.field.amountAlt': 'Amount (alternate currency)',

  'financial_instrument.actions.create': TEXTO_COMUN_EN.save,
  'financial_instrument.actions.update': 'Save changes',
  'financial_instrument.actions.delete': TEXTO_COMUN_EN.delete,

  'financial_instrument.delete.title': 'Delete financial instrument',
  'financial_instrument.delete.body':
    'The current financial instrument will be deactivated. You can capture a new one afterwards. Continue?',
  'financial_instrument.delete.confirm': TEXTO_COMUN_EN.delete,
  'financial_instrument.delete.cancel': TEXTO_COMUN_EN.cancel,

  'financial_instrument.validation.required': 'Required field.',
  'financial_instrument.validation.amountPositive': 'The amount must be greater than 0.',
  'financial_instrument.validation.dueDateBeforeIssue':
    'The due date must be equal to or later than the issue date.',

  'financial_instrument.error.forbidden': TEXTO_COMUN_EN.errorForbidden,
  'financial_instrument.error.conflict': 'An active financial instrument already exists for this document.',
  'financial_instrument.error.concurrency':
    'The instrument was modified by another user. The data was reloaded.',
  'financial_instrument.error.validation': 'Check the entered data.',
  'financial_instrument.error.notFound': 'Resource not found.',
  'financial_instrument.error.generic': TEXTO_COMUN_EN.errorGeneric,

  'financial_instrument.toast.created': 'Financial instrument created successfully.',
  'financial_instrument.toast.updated': 'Financial instrument updated successfully.',
  'financial_instrument.toast.deleted': 'Financial instrument deleted successfully.',

  'financial_instrument.factbox.title': 'Financial instrument',
  'financial_instrument.factbox.pending': 'Pending capture',
  'financial_instrument.factbox.number': 'No. {number}',
  'financial_instrument.factbox.due': 'Due {date}',
};

/** Diccionarios de respaldo por idioma, listos para registrar en el host si se desea. */
export const FINANCIAL_INSTRUMENT_TRANSLATIONS = {
  es: FINANCIAL_INSTRUMENT_TRANSLATIONS_ES,
  en: FINANCIAL_INSTRUMENT_TRANSLATIONS_EN,
} as const;

/** Función de traducción con interpolación `{var}`. */
export type FinancialInstrumentT = TranslateFn;

/**
 * Hook de traducción del widget.
 *
 * Usa `useTranslation` del kit; si la clave no está registrada en el host
 * (el motor devuelve la propia clave), cae al diccionario local del idioma activo.
 */
export const useFinancialInstrumentTranslation = createTranslationHook(
  FINANCIAL_INSTRUMENT_TRANSLATIONS,
);
